"use client";

import * as api from "@/lib/auth-api";
import { EmailFormValues } from "@/lib/auth-flow";
import { queryClient } from "@/lib/query-client";
import { useMutation } from "@tanstack/react-query";
import Link from "next/link";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AuthField, OrDivider, PasswordInput, SubmitButton } from "./auth-ui";
import { GoogleButton } from "./google-button";
import { EmailField } from "./email-field";

type LoginValues = EmailFormValues & { password: string };

export function LoginForm({ redirect }: { redirect: string }) {
  const form = useForm<LoginValues>({
    defaultValues: { email: "", password: "" },
  });

  const mutation = useMutation(
    (v: LoginValues) => api.login(v.email.trim(), v.password),
    {
      onSuccess: () => {
        queryClient.resetQueries();
        window.location.href = redirect;
      },
      onError: (e) => toast.error(api.getErrorMessage(e)),
    },
  );

  const serverErrors = api.getServerErrors(mutation.error);
  const passwordError = form.formState.errors.password?.message ?? serverErrors.password;

  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
        className="grid gap-4"
      >
        <EmailField serverErrors={serverErrors} />
        <AuthField label="Kata Sandi" htmlFor="password" error={passwordError}>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            invalid={!!passwordError}
            {...form.register("password", { required: "Kata sandi wajib diisi" })}
          />
        </AuthField>
        <div className="-mt-1 text-right">
          <Link
            href="/forgot-password"
            className="rounded text-xs text-theme-gray hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
          >
            Lupa Password?
          </Link>
        </div>
        <SubmitButton loading={mutation.isLoading || mutation.isSuccess}>Masuk</SubmitButton>
        <OrDivider />
        <GoogleButton redirect={redirect} />
      </form>
    </FormProvider>
  );
}
